/**
 * Shared Add/Edit User dialog for Radiologist and Technician.
 * Uses multipart/form-data for Add (POST /ris/api/auth/register). Edit uses JSON (PUT) or multipart when a new signature file is uploaded (Radiologist).
 * Sub-role (Junior/Senior) shown only when radiologist hierarchy is enabled.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { X, ChevronDown, Check, Eye } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@ohif/ui-next';
import {
  ICON_STROKE_WIDTH,
  GENDER_OPTIONS,
  MODALITY_OPTIONS,
  ROLE_OPTIONS,
  SPECIALTY_OPTIONS,
  STATUS_OPTIONS,
  SUB_ROLE_OPTIONS,
  isAllowedSignatureUploadFile,
  SIGNATURE_UPLOAD_ACCEPT,
  SIGNATURE_UPLOAD_TYPE_ERROR,
} from '../constants';
import Button from './Button';
import FormField from './common/FormField';
import RequiredLabel from './common/RequiredLabel';
import {
  getCreateUserValidationErrors,
  getEditUserValidationErrors,
} from '../utils/userFormValidation';
import {
  mapApiResponseToFormValues,
  extractFilenameFromPath,
  resolveSignatureAssetUrl,
} from '../utils/userMapper';
import { trimFormStringFields } from '../utils/trimFormStringFields';
import SignaturePreviewDialog from './SignaturePreviewDialog';
import {
  parseCsvValues,
  buildSubmitContext,
  runUserFormSubmit,
  evaluateUserFormSubmitGate,
  applyUserFormSubmitResult,
} from './userFormDialogSubmitHelpers';
import {
  UserFormStyledSelect,
  UserFormMobileField,
  UserFormDobField,
} from './userFormDialogFieldHelpers';
import type { User } from '../types/user.types';
import '../pages/superadmin/UserManagement.css';

export type UserFormMode = 'add' | 'edit';

export interface UserFormDialogProps {
  open: boolean;
  mode: UserFormMode;
  /** For edit mode: user to edit (from listing API). For add: null. */
  initialUser: User | null;
  facilityId: string | null;
  onClose: () => void;
  onSuccess: (user: User) => void;
}

const INITIAL_FORM: Record<string, string> = {
  role: '',
  employeeId: '',
  fullName: '',
  gender: '',
  dateOfBirth: '',
  email: '',
  mobileNumber: '',
  username: '',
  password: '',
  status: 'Active',
  department: '',
  shiftStartAt: '',
  shiftEndAt: '',
  experience: '',
  subRole: '',
  designation: '',
  registrationNumber: '',
  qualification: '',
  specialty: '',
  modalityAccess: '',
  availabilityHours: '',
  availabilityDays: '',
  specialization: '',
  clinicHospitalName: '',
  reportAccess: 'true',
};

const AVAILABILITY_DAYS_OPTIONS: { value: string; label: string }[] = [
  { value: 'monday', label: 'M' },
  { value: 'tuesday', label: 'T' },
  { value: 'wednesday', label: 'W' },
  { value: 'thursday', label: 'T' },
  { value: 'friday', label: 'F' },
  { value: 'saturday', label: 'S' },
  { value: 'sunday', label: 'S' },
];

function availabilityDaysDisplayToForm(display: string[]): string {
  const toLower: Record<string, string> = {
    Mon: 'monday',
    Tue: 'tuesday',
    Wed: 'wednesday',
    Thu: 'thursday',
    Fri: 'friday',
    Sat: 'saturday',
    Sun: 'sunday',
  };
  return (display || [])
    .map(d => toLower[d] || d.toLowerCase())
    .filter(Boolean)
    .join(',');
}

function getMobileDigits(raw: string | undefined): string {
  if (raw == null) {
    return '';
  }
  return String(raw).trim().replace(/\D/g, '').slice(-10);
}

/** Convert API date to dd/mm/yyyy for the form (editable format). */
function formatDashDateToDdMmYyyy(parts: string[]): string | null {
  if (parts.length !== 3) {
    return null;
  }
  if (parts[0].length === 4) {
    const [y, m, d] = parts;
    return `${d.padStart(2, '0')}/${m.padStart(2, '0')}/${y}`;
  }
  const [d, m, y] = parts;
  return `${d.padStart(2, '0')}/${m.padStart(2, '0')}/${y}`;
}

function formatSlashDateToDdMmYyyy(parts: string[]): string | null {
  if (parts.length !== 3) {
    return null;
  }
  const [a, b, c] = parts;
  const day = a.length <= 2 ? a.padStart(2, '0') : a.slice(0, 2);
  const month = b.length <= 2 ? b.padStart(2, '0') : b.slice(0, 2);
  const year = c.length === 4 ? c : c.slice(0, 4);
  return `${day}/${month}/${year}`;
}

function apiDobToDdMmYyyy(api: string | undefined): string {
  if (!api || !api.trim()) {
    return '';
  }
  const s = api.trim();
  if (s.includes('-')) {
    return formatDashDateToDdMmYyyy(s.split('-')) ?? s;
  }
  if (s.includes('/')) {
    return formatSlashDateToDdMmYyyy(s.split('/').map(p => p.trim())) ?? s;
  }
  return s;
}

/** Convert dd/mm/yyyy form value to yyyy-mm-dd for API. */
function ddMmYyyyToApi(ddmm: string | undefined): string {
  if (!ddmm || !ddmm.trim()) {
    return '';
  }
  const parts = ddmm
    .trim()
    .split('/')
    .map(p => p.trim());
  if (parts.length !== 3) {
    return ddmm.trim();
  }
  const [dd, mm, yyyy] = parts;
  if (dd.length >= 1 && dd.length <= 2 && mm.length >= 1 && mm.length <= 2 && yyyy.length === 4) {
    return `${yyyy}-${mm.padStart(2, '0')}-${dd.padStart(2, '0')}`;
  }
  return ddmm.trim();
}

/** Return date_of_birth for API payload: only valid yyyy-mm-dd, else empty string. Avoids sending partial/invalid dates. */
function getDobForPayload(formDateOfBirth: string | undefined): string {
  if (!formDateOfBirth || !formDateOfBirth.trim()) {
    return '';
  }
  const iso = ddMmYyyyToApi(formDateOfBirth.trim());
  if (/^\d{4}-\d{2}-\d{2}$/.test(iso)) {
    return iso;
  }
  return '';
}

/** Enforce dd/mm/yyyy: only digits (and optional slashes), year max 4 digits. */
function formatDobInput(value: string): string {
  const digits = value.replace(/\D/g, '');
  const dd = digits.slice(0, 2);
  const mm = digits.slice(2, 4);
  const yyyy = digits.slice(4, 8);
  if (yyyy.length) {
    return `${dd}/${mm}/${yyyy}`;
  }
  if (mm.length) {
    return `${dd}/${mm}`;
  }
  return dd;
}

/** Return yyyy-mm-dd for native date input when form has complete dd/mm/yyyy, else ''. */
function dobToNativeValue(ddmm: string | undefined): string {
  if (!ddmm || !ddmm.trim()) {
    return '';
  }
  const api = ddMmYyyyToApi(ddmm.trim());
  if (api.length === 10 && api.includes('-')) {
    return api;
  }
  return '';
}

/** Toggle a day token in availability CSV (comma-joined, no spaces). */
function toggleAvailabilityDayValue(
  availabilityDaysCsv: string,
  dayValue: string,
  currentlySelected: boolean
): string {
  const current = parseCsvValues(availabilityDaysCsv);
  const next = currentlySelected
    ? current.filter(dayToken => dayToken !== dayValue)
    : [...current, dayValue];
  return next.join(',');
}

/** Remove one exact token from a comma-separated field (join with ', '). */
function removeCsvToken(csv: string, tokenToRemove: string): string {
  return parseCsvValues(csv)
    .filter(token => token !== tokenToRemove)
    .join(', ');
}

/** Append a token to a comma-separated field (join with ', '). */
function appendCsvToken(csv: string, tokenToAdd: string): string {
  return [...parseCsvValues(csv), tokenToAdd].join(', ');
}

function formatReportAccessLabel(value: string | undefined): string {
  if (value === 'true') {
    return 'Yes';
  }
  if (value === 'false') {
    return 'No';
  }
  return 'Select Report Access';
}

type UserFormDialogFormCtx = Record<string, any>;

const UserFormDialogFormContext = React.createContext<UserFormDialogFormCtx | null>(null);

function useUserFormDialogFormCtx(): UserFormDialogFormCtx {
  const ctx = React.useContext(UserFormDialogFormContext);
  if (!ctx) {
    throw new Error('UserFormDialogFormContext missing');
  }
  return ctx;
}

function UserFormRoleFields() {
  const ctx = useUserFormDialogFormCtx();

  return (
    <>
      {/* Role */}
      <div
        className="create-user-dialog-field create-user-dialog-field-role"
        ref={ctx.roleDropdownRef}
      >
        <RequiredLabel
          label="Role"
          required
        />
        {ctx.isEdit ? (
          <div
            className="create-user-select-wrap create-user-select-wrap-readonly"
            aria-readonly
          >
            <span className="create-user-select-value">{ctx.form.role || '—'}</span>
          </div>
        ) : (
          <>
            <div
              className={`create-user-select-wrap ${ctx.roleDropdownOpen ? 'create-user-select-wrap-open' : ''} ${ctx.touched && ctx.errors.role ? 'create-user-dialog-input-error' : ''}`}
              onClick={() => ctx.setRoleDropdownOpen(!ctx.roleDropdownOpen)}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  ctx.setRoleDropdownOpen(!ctx.roleDropdownOpen);
                }
                if (e.key === 'Escape') {
                  ctx.setRoleDropdownOpen(false);
                }
              }}
              role="combobox"
              aria-expanded={ctx.roleDropdownOpen}
              aria-haspopup="listbox"
              aria-invalid={!!(ctx.touched && ctx.errors.role)}
              tabIndex={0}
            >
              <span
                className={
                  ctx.form.role ? 'create-user-select-value' : 'create-user-select-placeholder'
                }
              >
                {ctx.form.role
                  ? (ROLE_OPTIONS.find(o => o.value === ctx.form.role)?.label ?? ctx.form.role)
                  : 'Select Role'}
              </span>
              <ChevronDown
                className="create-user-select-chevron"
                size={18}
                strokeWidth={2}
                aria-hidden
              />
            </div>
            {ctx.roleDropdownOpen && (
              <div
                className="create-user-select-dropdown"
                role="listbox"
                onMouseDown={e => e.preventDefault()}
              >
                {ROLE_OPTIONS.map(opt => (
                  <div
                    key={opt.value}
                    role="option"
                    aria-selected={ctx.form.role === opt.value}
                    className="create-user-select-option"
                    onMouseDown={e => {
                      e.preventDefault();
                      ctx.setFormField('role', opt.value);
                      ctx.setRoleDropdownOpen(false);
                    }}
                  >
                    {opt.label}
                  </div>
                ))}
              </div>
            )}
          </>
        )}
        {ctx.touched && ctx.errors.role && (
          <span className="create-user-dialog-field-error">{ctx.errors.role}</span>
        )}
      </div>
    </>
  );
}

function UserFormSubRoleFields() {
  const ctx = useUserFormDialogFormCtx();

  return (
    <>
      {/* Sub-role (Radiologist, hierarchy enabled only) */}
      {ctx.showSubRole && (
        <div
          className="create-user-dialog-field create-user-dialog-field-styled-dropdown"
          ref={ctx.subRoleDropdownRef}
        >
          <RequiredLabel
            label="Sub-role"
            required
          />
          <div
            className={`create-user-select-wrap ${ctx.subRoleDropdownOpen ? 'create-user-select-wrap-open' : ''} ${ctx.touched && ctx.errors.subRole ? 'create-user-dialog-input-error' : ''}`}
            onClick={() => ctx.setSubRoleDropdownOpen(!ctx.subRoleDropdownOpen)}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                ctx.setSubRoleDropdownOpen(!ctx.subRoleDropdownOpen);
              }
              if (e.key === 'Escape') {
                ctx.setSubRoleDropdownOpen(false);
              }
            }}
            role="combobox"
            aria-expanded={ctx.subRoleDropdownOpen}
            aria-haspopup="listbox"
            aria-invalid={!!(ctx.touched && ctx.errors.subRole)}
            tabIndex={0}
          >
            <span
              className={
                ctx.form.subRole ? 'create-user-select-value' : 'create-user-select-placeholder'
              }
            >
              {ctx.form.subRole
                ? (SUB_ROLE_OPTIONS.find(o => o.value === ctx.form.subRole)?.label ??
                  ctx.form.subRole)
                : 'Select Sub-role'}
            </span>
            <ChevronDown
              className="create-user-select-chevron"
              size={18}
              strokeWidth={2}
              aria-hidden
            />
          </div>
          {ctx.subRoleDropdownOpen && (
            <div
              className="create-user-select-dropdown"
              role="listbox"
              onMouseDown={e => e.preventDefault()}
            >
              {SUB_ROLE_OPTIONS.map(opt => (
                <div
                  key={opt.value}
                  role="option"
                  aria-selected={ctx.form.subRole === opt.value}
                  className="create-user-select-option"
                  onMouseDown={e => {
                    e.preventDefault();
                    ctx.setFormField('subRole', opt.value);
                    ctx.setSubRoleDropdownOpen(false);
                  }}
                >
                  {opt.label}
                </div>
              ))}
            </div>
          )}
          {ctx.touched && ctx.errors.subRole && (
            <span className="create-user-dialog-field-error">{ctx.errors.subRole}</span>
          )}
        </div>
      )}
    </>
  );
}

function UserFormReferringPhysicianGeneralDetails() {
  const ctx = useUserFormDialogFormCtx();

  return (
    <>
      <div className="create-user-dialog-grid">
        <FormField
          label="Full Name *"
          required
          value={ctx.form.fullName}
          onChange={e => ctx.setFormField('fullName', e.target.value)}
          onBlur={ctx.trimFormFieldOnBlur('fullName')}
          placeholder="John Doe"
          error={ctx.touched ? ctx.errors.fullName : undefined}
        />
        <UserFormStyledSelect
          label="Gender"
          required
          open={ctx.genderDropdownOpen}
          setOpen={ctx.setGenderDropdownOpen}
          containerRef={ctx.genderDropdownRef}
          value={ctx.form.gender}
          options={GENDER_OPTIONS}
          placeholder="Select Gender"
          error={ctx.errors.gender}
          showError={ctx.touched}
          onSelect={v => ctx.setFormField('gender', v)}
        />
        <UserFormDobField
          value={ctx.form.dateOfBirth}
          onTextChange={v => ctx.setFormField('dateOfBirth', v)}
          onBlur={ctx.trimFormFieldOnBlur('dateOfBirth')}
          onNativeChange={v => ctx.setFormField('dateOfBirth', v)}
          formatDobInput={formatDobInput}
          dobToNativeValue={dobToNativeValue}
          apiDobToDdMmYyyy={apiDobToDdMmYyyy}
          pickerRef={ctx.dobPickerInputRef}
          error={ctx.errors.dateOfBirth}
          showError={ctx.touched}
        />
        <FormField
          label="Email *"
          type="email"
          required
          value={ctx.form.email}
          readOnly={ctx.isEdit}
          onChange={e => !ctx.isEdit && ctx.setFormField('email', e.target.value)}
          onBlur={ctx.trimFormFieldOnBlur('email')}
          placeholder="john.doe@radiq.com"
          error={ctx.touched ? ctx.errors.email : undefined}
        />
      </div>
      <div className="create-user-dialog-grid">
        <UserFormMobileField
          value={ctx.form.mobileNumber}
          onChange={v => ctx.setFormField('mobileNumber', v)}
          onBlur={ctx.trimFormFieldOnBlur('mobileNumber')}
          error={ctx.errors.mobileNumber}
          showError={ctx.touched}
        />
        <FormField
          label="Username *"
          required
          value={ctx.form.username}
          readOnly={ctx.isEdit}
          onChange={e => !ctx.isEdit && ctx.setFormField('username', e.target.value)}
          onBlur={ctx.trimFormFieldOnBlur('username')}
          placeholder="Enter Username"
          error={ctx.touched ? ctx.errors.username : undefined}
        />
      </div>
      <div className="create-user-dialog-grid">
        {!ctx.isEdit && (
          <FormField
            label="Password *"
            type="password"
            required
            value={ctx.form.password}
            onChange={e => ctx.setFormField('password', e.target.value)}
            placeholder="Enter Password"
            error={ctx.touched ? ctx.errors.password : undefined}
          />
        )}
        <UserFormStyledSelect
          label="Status"
          required
          open={ctx.statusDropdownOpen}
          setOpen={ctx.setStatusDropdownOpen}
          containerRef={ctx.statusDropdownRef}
          value={ctx.form.status}
          options={STATUS_OPTIONS}
          placeholder="Select Status"
          error={ctx.errors.status}
          showError={ctx.touched}
          onSelect={v => ctx.setFormField('status', v)}
        />
      </div>
    </>
  );
}

function UserFormStaffGeneralDetails() {
  const ctx = useUserFormDialogFormCtx();

  return (
    <>
      <div className="create-user-dialog-grid">
        <div className="create-user-dialog-field">
          <RequiredLabel
            label="Employee ID"
            required
          />
          <input
            type="text"
            className={['create-user-dialog-input', ctx.isEdit ? 'edit-user-input-disabled' : '']
              .filter(Boolean)
              .join(' ')}
            value={ctx.form.employeeId}
            readOnly={ctx.isEdit}
            onChange={e => !ctx.isEdit && ctx.setFormField('employeeId', e.target.value)}
            onBlur={ctx.trimFormFieldOnBlur('employeeId')}
            placeholder="EMP-001"
          />
        </div>
        <UserFormStyledSelect
          label="Gender"
          required
          open={ctx.genderDropdownOpen}
          setOpen={ctx.setGenderDropdownOpen}
          containerRef={ctx.genderDropdownRef}
          value={ctx.form.gender}
          options={GENDER_OPTIONS}
          placeholder="Select Gender"
          error={ctx.errors.gender}
          showError={ctx.touched}
          onSelect={v => ctx.setFormField('gender', v)}
        />
        <FormField
          label="Full Name *"
          required
          value={ctx.form.fullName}
          onChange={e => ctx.setFormField('fullName', e.target.value)}
          onBlur={ctx.trimFormFieldOnBlur('fullName')}
          placeholder="John Doe"
          error={ctx.touched ? ctx.errors.fullName : undefined}
        />
        <UserFormDobField
          value={ctx.form.dateOfBirth}
          onTextChange={v => ctx.setFormField('dateOfBirth', v)}
          onBlur={ctx.trimFormFieldOnBlur('dateOfBirth')}
          onNativeChange={v => ctx.setFormField('dateOfBirth', v)}
          formatDobInput={formatDobInput}
          dobToNativeValue={dobToNativeValue}
          apiDobToDdMmYyyy={apiDobToDdMmYyyy}
          pickerRef={ctx.dobPickerInputRef}
          error={ctx.errors.dateOfBirth}
          showError={ctx.touched}
        />
      </div>
      <div className="create-user-dialog-grid">
        <FormField
          label="Email *"
          type="email"
          required
          value={ctx.form.email}
          readOnly={ctx.isEdit}
          onChange={e => !ctx.isEdit && ctx.setFormField('email', e.target.value)}
          onBlur={ctx.trimFormFieldOnBlur('email')}
          placeholder="john.doe@radiq.com"
          error={ctx.touched ? ctx.errors.email : undefined}
        />
        <UserFormMobileField
          value={ctx.form.mobileNumber}
          onChange={v => ctx.setFormField('mobileNumber', v)}
          onBlur={ctx.trimFormFieldOnBlur('mobileNumber')}
          error={ctx.errors.mobileNumber}
          showError={ctx.touched}
        />
        <FormField
          label="Username *"
          required
          value={ctx.form.username}
          readOnly={ctx.isEdit}
          onChange={e => !ctx.isEdit && ctx.setFormField('username', e.target.value)}
          onBlur={ctx.trimFormFieldOnBlur('username')}
          placeholder="Enter Username"
          error={ctx.touched ? ctx.errors.username : undefined}
        />
        {ctx.isEdit ? (
          <div />
        ) : (
          <FormField
            label="Password *"
            type="password"
            required
            value={ctx.form.password}
            onChange={e => ctx.setFormField('password', e.target.value)}
            placeholder="Enter Password"
            error={ctx.touched ? ctx.errors.password : undefined}
          />
        )}
      </div>
      <div className="create-user-dialog-grid">
        <UserFormStyledSelect
          label="Status"
          required
          open={ctx.statusDropdownOpen}
          setOpen={ctx.setStatusDropdownOpen}
          containerRef={ctx.statusDropdownRef}
          value={ctx.form.status}
          options={STATUS_OPTIONS}
          placeholder="Select Status"
          error={ctx.errors.status}
          showError={ctx.touched}
          onSelect={v => ctx.setFormField('status', v)}
        />
        <div />
      </div>
    </>
  );
}

function UserFormGeneralDetailsFields() {
  const { form } = useUserFormDialogFormCtx();

  return (
    <>
      <h3 className="create-user-dialog-section-title create-user-dialog-section-title-with-divider">
        General Details
      </h3>
      {form.role === 'ReferringPhysician' ? (
        <UserFormReferringPhysicianGeneralDetails />
      ) : (
        <UserFormStaffGeneralDetails />
      )}
    </>
  );
}

function UserFormTechnicianCredentials() {
  const ctx = useUserFormDialogFormCtx();

  return (
    <>
      {/* Technician: Professional Credentials */}
      {ctx.form.role === 'Technician' && (
        <>
          <h3 className="create-user-dialog-section-title create-user-dialog-section-title-with-divider">
            Professional Credentials
          </h3>
          <div className="create-user-dialog-grid">
            <FormField
              label="Shift start at *"
              type="time"
              required
              value={ctx.form.shiftStartAt || ''}
              onChange={e => ctx.setFormField('shiftStartAt', e.target.value)}
              onBlur={ctx.trimFormFieldOnBlur('shiftStartAt')}
              placeholder="--:--"
              error={ctx.touched ? ctx.errors.shiftStartAt : undefined}
            />
            <FormField
              label="Shift end at *"
              type="time"
              required
              value={ctx.form.shiftEndAt || ''}
              onChange={e => ctx.setFormField('shiftEndAt', e.target.value)}
              onBlur={ctx.trimFormFieldOnBlur('shiftEndAt')}
              placeholder="--:--"
              error={ctx.touched ? ctx.errors.shiftEndAt : undefined}
            />
          </div>
          <div className="create-user-dialog-grid">
            <FormField
              label="Experience (in Years)"
              inputMode="numeric"
              autoComplete="off"
              value={ctx.form.experience || ''}
              onChange={e =>
                ctx.setFormField('experience', e.target.value.replaceAll(/\D/g, '').slice(0, 3))
              }
              onBlur={ctx.trimFormFieldOnBlur('experience')}
              placeholder="e.g., 5"
              error={ctx.touched ? ctx.errors.experience : undefined}
            />
            <div />
          </div>
        </>
      )}
    </>
  );
}

function UserFormRadiologistCredentials() {
  const ctx = useUserFormDialogFormCtx();

  return (
    <>
      {/* Radiologist: Professional Credentials */}
      {ctx.form.role === 'Radiologist' && (
        <>
          <h3 className="create-user-dialog-section-title create-user-dialog-section-title-with-divider">
            Professional Credentials
          </h3>
          <div className="create-user-dialog-grid">
            <FormField
              label="Shift start at *"
              type="time"
              required
              value={ctx.form.shiftStartAt || ''}
              onChange={e => ctx.setFormField('shiftStartAt', e.target.value)}
              onBlur={ctx.trimFormFieldOnBlur('shiftStartAt')}
              placeholder="--:--"
              error={ctx.touched ? ctx.errors.shiftStartAt : undefined}
            />
            <FormField
              label="Shift end at *"
              type="time"
              required
              value={ctx.form.shiftEndAt || ''}
              onChange={e => ctx.setFormField('shiftEndAt', e.target.value)}
              onBlur={ctx.trimFormFieldOnBlur('shiftEndAt')}
              placeholder="--:--"
              error={ctx.touched ? ctx.errors.shiftEndAt : undefined}
            />
          </div>
          <div className="create-user-dialog-grid">
            <FormField
              label="Designation *"
              required
              value={ctx.form.designation || ''}
              onChange={e => ctx.setFormField('designation', e.target.value)}
              onBlur={ctx.trimFormFieldOnBlur('designation')}
              placeholder="e.g., Junior Radiologist, Senior Radiologist"
              error={ctx.touched ? ctx.errors.designation : undefined}
            />
            <FormField
              label="Registration No. *"
              required
              value={ctx.form.registrationNumber || ''}
              onChange={e => ctx.setFormField('registrationNumber', e.target.value)}
              onBlur={ctx.trimFormFieldOnBlur('registrationNumber')}
              placeholder="REG-12345"
              error={ctx.touched ? ctx.errors.registrationNumber : undefined}
            />
          </div>
          <div className="create-user-dialog-grid">
            <FormField
              label="Qualification *"
              required
              value={ctx.form.qualification || ''}
              onChange={e => ctx.setFormField('qualification', e.target.value)}
              onBlur={ctx.trimFormFieldOnBlur('qualification')}
              placeholder="e.g. MD"
              error={ctx.touched ? ctx.errors.qualification : undefined}
            />
            <FormField
              label="Experience (in Years)"
              inputMode="numeric"
              autoComplete="off"
              value={ctx.form.experience || ''}
              onChange={e =>
                ctx.setFormField('experience', e.target.value.replaceAll(/\D/g, '').slice(0, 3))
              }
              onBlur={ctx.trimFormFieldOnBlur('experience')}
              placeholder="e.g., 5"
              error={ctx.touched ? ctx.errors.experience : undefined}
            />
          </div>
          <div className="create-user-dialog-field">
            <RequiredLabel
              label="Availability Days"
              required
            />
            <div className="create-user-availability-days-wrap">
              {AVAILABILITY_DAYS_OPTIONS.map(day => {
                const selected = ctx
                  .parseCsvValues(ctx.form.availabilityDays || '')
                  .includes(day.value);
                return (
                  <button
                    key={day.value}
                    type="button"
                    className={`create-user-availability-day-btn ${selected ? 'create-user-availability-day-btn-selected' : ''}`}
                    onClick={() =>
                      ctx.setFormField(
                        'availabilityDays',
                        ctx.toggleAvailabilityDayValue(
                          ctx.form.availabilityDays || '',
                          day.value,
                          selected
                        )
                      )
                    }
                    aria-pressed={selected}
                    aria-label={day.value}
                  >
                    {day.label}
                  </button>
                );
              })}
            </div>
          </div>
          <div
            className="create-user-dialog-field create-user-dialog-field-styled-dropdown"
            ref={ctx.specialtyDropdownRef}
          >
            <RequiredLabel label="Specialty" />
            <div
              className={`create-user-select-wrap create-user-modality-select-wrap ${ctx.specialtyDropdownOpen ? 'create-user-select-wrap-open' : ''}`}
              onClick={() =>
                ctx.setSpecialtyDropdownOpen(prev => {
                  if (!prev) {
                    ctx.setSpecialtySearchQuery('');
                  }
                  return !prev;
                })
              }
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  ctx.setSpecialtyDropdownOpen(prev => {
                    if (!prev) {
                      ctx.setSpecialtySearchQuery('');
                    }
                    return !prev;
                  });
                }
                if (e.key === 'Escape') {
                  ctx.setSpecialtyDropdownOpen(false);
                }
              }}
              role="combobox"
              aria-expanded={ctx.specialtyDropdownOpen}
              aria-haspopup="listbox"
              tabIndex={0}
            >
              <div className="create-user-modality-inner">
                <div className="edit-user-modality-chips">
                  {ctx.parseCsvValues(ctx.form.specialty).map(selectedSpecialty => {
                    const chipLabel = ctx.specialtyOptionLabelForChip(selectedSpecialty);
                    return (
                      <span
                        key={selectedSpecialty}
                        className="edit-user-modality-chip"
                      >
                        {chipLabel}
                        <button
                          type="button"
                          className="edit-user-modality-chip-remove"
                          onClick={e => {
                            e.stopPropagation();
                            ctx.setFormField(
                              'specialty',
                              removeCsvToken(ctx.form.specialty, selectedSpecialty)
                            );
                          }}
                          aria-label={`Remove ${chipLabel}`}
                        >
                          <X
                            size={12}
                            strokeWidth={2}
                          />
                        </button>
                      </span>
                    );
                  })}
                </div>
                <span
                  className={
                    ctx.parseCsvValues(ctx.form.specialty).length
                      ? 'create-user-select-value'
                      : 'create-user-select-placeholder'
                  }
                >
                  {ctx.parseCsvValues(ctx.form.specialty).length === 0 ? 'Select Specialty' : ''}
                </span>
                <ChevronDown
                  className="create-user-select-chevron"
                  size={18}
                  strokeWidth={2}
                  aria-hidden
                />
              </div>
            </div>
            {ctx.specialtyDropdownOpen && (
              <div
                className="create-user-select-dropdown"
                role="listbox"
                onMouseDown={e => e.preventDefault()}
              >
                <div className="create-user-select-free-text-wrap">
                  <input
                    type="text"
                    value={ctx.specialtySearchQuery}
                    onChange={e => ctx.setSpecialtySearchQuery(e.target.value)}
                    onBlur={ctx.trimSearchQueryStateOnBlur(ctx.setSpecialtySearchQuery)}
                    onMouseDown={e => e.stopPropagation()}
                    className="create-user-select-free-text-input"
                    placeholder="Enter Text Here..."
                    aria-label="Search or add specialty"
                  />
                </div>
                {ctx.shouldShowAddCustomSpecialty(ctx.specialtySearchQuery, ctx.form.specialty) && (
                  <div
                    role="option"
                    className="create-user-select-option create-user-modality-option create-user-specialty-add-option"
                    onMouseDown={e => {
                      e.preventDefault();
                      const trimmedCustomSpecialty = ctx.specialtySearchQuery.trim();
                      ctx.setFormField(
                        'specialty',
                        ctx.appendSpecialtyCustomToCsv(ctx.form.specialty, trimmedCustomSpecialty)
                      );
                      ctx.setSpecialtySearchQuery('');
                    }}
                  >
                    <span
                      className="create-user-modality-option-check"
                      aria-hidden
                    />
                    <span className="create-user-modality-option-label">
                      Add &quot;{ctx.specialtySearchQuery.trim()}&quot;
                    </span>
                  </div>
                )}
                {ctx.filterSpecialtyOptionsByQuery(ctx.specialtySearchQuery).map(opt => {
                  const selected = ctx.csvHasValueCaseInsensitive(ctx.form.specialty, opt.value);
                  return (
                    <div
                      key={opt.value}
                      role="option"
                      aria-selected={selected}
                      className={[
                        'create-user-select-option',
                        'create-user-modality-option',
                        selected ? 'create-user-modality-option--selected-toggle' : '',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                      onMouseDown={e => {
                        e.preventDefault();
                        ctx.setFormField(
                          'specialty',
                          ctx.toggleSpecialtyInCsvCaseInsensitive(ctx.form.specialty, opt.value)
                        );
                        ctx.setSpecialtySearchQuery('');
                      }}
                    >
                      <span
                        className="create-user-modality-option-check"
                        aria-hidden
                      >
                        {selected ? (
                          <Check
                            size={14}
                            strokeWidth={2.5}
                            className="create-user-modality-check"
                          />
                        ) : null}
                      </span>
                      <span className="create-user-modality-option-label">{opt.label}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          <div
            className="create-user-dialog-field create-user-dialog-field-styled-dropdown"
            ref={ctx.modalityDropdownRef}
          >
            <RequiredLabel
              label="Reporting Modality Access"
              required
            />
            <div
              className={`create-user-select-wrap create-user-modality-select-wrap ${ctx.modalityDropdownOpen ? 'create-user-select-wrap-open' : ''} ${ctx.touched && ctx.errors.modalityAccess ? 'create-user-dialog-input-error' : ''}`}
              onClick={() => ctx.setModalityDropdownOpen(!ctx.modalityDropdownOpen)}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  ctx.setModalityDropdownOpen(!ctx.modalityDropdownOpen);
                }
                if (e.key === 'Escape') {
                  ctx.setModalityDropdownOpen(false);
                }
              }}
              role="combobox"
              aria-expanded={ctx.modalityDropdownOpen}
              aria-haspopup="listbox"
              aria-invalid={!!(ctx.touched && ctx.errors.modalityAccess)}
              tabIndex={0}
            >
              <div className="create-user-modality-inner">
                <div className="edit-user-modality-chips">
                  {ctx.parseCsvValues(ctx.form.modalityAccess || '').map(m => (
                    <span
                      key={m}
                      className="edit-user-modality-chip"
                    >
                      {m}
                      <button
                        type="button"
                        className="edit-user-modality-chip-remove"
                        onClick={e => {
                          e.stopPropagation();
                          ctx.setFormField(
                            'modalityAccess',
                            removeCsvToken(ctx.form.modalityAccess || '', m)
                          );
                        }}
                        aria-label={`Remove ${m}`}
                      >
                        <X
                          size={12}
                          strokeWidth={2}
                        />
                      </button>
                    </span>
                  ))}
                </div>
                <span
                  className={
                    ctx.form.modalityAccess?.trim()
                      ? 'create-user-select-value'
                      : 'create-user-select-placeholder'
                  }
                >
                  {ctx.parseCsvValues(ctx.form.modalityAccess || '').length === 0
                    ? 'Select options...'
                    : ''}
                </span>
                <ChevronDown
                  className="create-user-select-chevron"
                  size={18}
                  strokeWidth={2}
                  aria-hidden
                />
              </div>
            </div>
            {ctx.modalityDropdownOpen && (
              <div
                className="create-user-select-dropdown"
                role="listbox"
                onMouseDown={e => e.preventDefault()}
              >
                {MODALITY_OPTIONS.map(opt => {
                  const selected = ctx
                    .parseCsvValues(ctx.form.modalityAccess || '')
                    .includes(opt.value);
                  return (
                    <div
                      key={opt.value}
                      role="option"
                      aria-selected={selected}
                      aria-disabled={selected}
                      className={[
                        'create-user-select-option',
                        'create-user-modality-option',
                        selected ? 'create-user-select-option-disabled' : '',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                      onMouseDown={e => {
                        if (selected) {
                          e.preventDefault();
                          return;
                        }
                        e.preventDefault();
                        ctx.setFormField(
                          'modalityAccess',
                          appendCsvToken(ctx.form.modalityAccess || '', opt.value)
                        );
                      }}
                    >
                      <span
                        className="create-user-modality-option-check"
                        aria-hidden
                      >
                        {selected ? (
                          <Check
                            size={14}
                            strokeWidth={2.5}
                            className="create-user-modality-check"
                          />
                        ) : null}
                      </span>
                      <span className="create-user-modality-option-label">{opt.label}</span>
                    </div>
                  );
                })}
              </div>
            )}
            {ctx.touched && ctx.errors.modalityAccess && (
              <span className="create-user-dialog-field-error">{ctx.errors.modalityAccess}</span>
            )}
          </div>
          <div className="create-user-dialog-field create-user-signature-field">
            <div className="create-user-signature-label-row">
              <RequiredLabel label="Signature" />
              {ctx.canOpenSignaturePreview && (
                <button
                  type="button"
                  className="create-user-signature-preview-btn"
                  onClick={ctx.openSignaturePreview}
                  aria-label="Preview signature file"
                  title="Preview signature"
                >
                  <Eye
                    size={18}
                    strokeWidth={ICON_STROKE_WIDTH}
                    aria-hidden
                  />
                </button>
              )}
            </div>
            <div className="create-user-signature-upload">
              <input
                id="create-user-signature-file"
                type="file"
                accept={SIGNATURE_UPLOAD_ACCEPT}
                className="create-user-signature-input-hidden"
                onChange={e => {
                  const f = e.target.files?.[0];
                  if (f && !isAllowedSignatureUploadFile(f)) {
                    e.target.value = '';
                    ctx.signatureFileRef.current = null;
                    ctx.setSelectedSignatureFileName('');
                    ctx.setSignatureFileError(SIGNATURE_UPLOAD_TYPE_ERROR);
                    return;
                  }
                  ctx.setSignatureFileError(null);
                  ctx.signatureFileRef.current = f ?? null;
                  ctx.setSelectedSignatureFileName(f ? f.name : '');
                  if (f) {
                    ctx.setSignatureCleared(false);
                  }
                }}
              />
              <div className="create-user-signature-row">
                <div className="create-user-signature-filename-box">
                  {ctx.selectedSignatureFileName || 'No file chosen'}
                </div>
                <button
                  type="button"
                  className="create-user-signature-upload-btn"
                  onClick={() => document.getElementById('create-user-signature-file')?.click()}
                >
                  Upload
                </button>
              </div>
              {ctx.canShowClearSignature && (
                <button
                  type="button"
                  className="create-user-signature-clear-btn"
                  onClick={ctx.handleClearSignature}
                >
                  Clear
                </button>
              )}
              {ctx.signatureFileError && (
                <span className="create-user-dialog-field-error">{ctx.signatureFileError}</span>
              )}
            </div>
          </div>
        </>
      )}
    </>
  );
}

function UserFormReferringPhysicianSection() {
  const ctx = useUserFormDialogFormCtx();

  return (
    <>
      {/* Referring Physician: Basic Information and Access Control */}
      {ctx.form.role === 'ReferringPhysician' && (
        <>
          <h3 className="create-user-dialog-section-title create-user-dialog-section-title-with-divider">
            Basic Information
          </h3>
          <div className="create-user-dialog-grid">
            <FormField
              label="Qualification"
              value={ctx.form.qualification || ''}
              onChange={e => ctx.setFormField('qualification', e.target.value)}
              onBlur={ctx.trimFormFieldOnBlur('qualification')}
              placeholder="e.g., MD"
            />
            <div
              className="create-user-dialog-field create-user-dialog-field-styled-dropdown"
              ref={ctx.specialtyDropdownRef}
            >
              <RequiredLabel label="Specialization" />
              <div
                className={`create-user-select-wrap create-user-modality-select-wrap ${ctx.specialtyDropdownOpen ? 'create-user-select-wrap-open' : ''}`}
                onClick={() =>
                  ctx.setSpecialtyDropdownOpen(prev => {
                    if (!prev) {
                      ctx.setSpecializationSearchQuery('');
                    }
                    return !prev;
                  })
                }
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    ctx.setSpecialtyDropdownOpen(prev => {
                      if (!prev) {
                        ctx.setSpecializationSearchQuery('');
                      }
                      return !prev;
                    });
                  }
                  if (e.key === 'Escape') {
                    ctx.setSpecialtyDropdownOpen(false);
                  }
                }}
                role="combobox"
                aria-expanded={ctx.specialtyDropdownOpen}
                aria-haspopup="listbox"
                tabIndex={0}
              >
                <div className="create-user-modality-inner">
                  <div className="edit-user-modality-chips">
                    {ctx.parseCsvValues(ctx.form.specialization).map(selectedSpecialization => {
                      const chipLabel = ctx.specialtyOptionLabelForChip(selectedSpecialization);
                      return (
                        <span
                          key={selectedSpecialization}
                          className="edit-user-modality-chip"
                        >
                          {chipLabel}
                          <button
                            type="button"
                            className="edit-user-modality-chip-remove"
                            onClick={e => {
                              e.stopPropagation();
                              ctx.setFormField(
                                'specialization',
                                removeCsvToken(ctx.form.specialization, selectedSpecialization)
                              );
                            }}
                            aria-label={`Remove ${chipLabel}`}
                          >
                            <X
                              size={12}
                              strokeWidth={2}
                            />
                          </button>
                        </span>
                      );
                    })}
                  </div>
                  <span
                    className={
                      ctx.parseCsvValues(ctx.form.specialization).length
                        ? 'create-user-select-value'
                        : 'create-user-select-placeholder'
                    }
                  >
                    {ctx.parseCsvValues(ctx.form.specialization).length === 0
                      ? 'Select Specialization'
                      : ''}
                  </span>
                  <ChevronDown
                    className="create-user-select-chevron"
                    size={18}
                    strokeWidth={2}
                    aria-hidden
                  />
                </div>
              </div>
              {ctx.specialtyDropdownOpen && (
                <div
                  className="create-user-select-dropdown"
                  role="listbox"
                  onMouseDown={e => e.preventDefault()}
                >
                  <div className="create-user-select-free-text-wrap">
                    <input
                      type="text"
                      value={ctx.specializationSearchQuery}
                      onChange={e => ctx.setSpecializationSearchQuery(e.target.value)}
                      onBlur={ctx.trimSearchQueryStateOnBlur(ctx.setSpecializationSearchQuery)}
                      onMouseDown={e => e.stopPropagation()}
                      className="create-user-select-free-text-input"
                      placeholder="Enter Text Here..."
                      aria-label="Search or add specialization"
                    />
                  </div>
                  {ctx.shouldShowAddCustomSpecialty(
                    ctx.specializationSearchQuery,
                    ctx.form.specialization
                  ) && (
                    <div
                      role="option"
                      className="create-user-select-option create-user-modality-option create-user-specialty-add-option"
                      onMouseDown={e => {
                        e.preventDefault();
                        const trimmedCustomSpecialization = ctx.specializationSearchQuery.trim();
                        ctx.setFormField(
                          'specialization',
                          ctx.appendSpecialtyCustomToCsv(
                            ctx.form.specialization,
                            trimmedCustomSpecialization
                          )
                        );
                        ctx.setSpecializationSearchQuery('');
                      }}
                    >
                      <span
                        className="create-user-modality-option-check"
                        aria-hidden
                      />
                      <span className="create-user-modality-option-label">
                        Add &quot;{ctx.specializationSearchQuery.trim()}&quot;
                      </span>
                    </div>
                  )}
                  {ctx.filterSpecialtyOptionsByQuery(ctx.specializationSearchQuery).map(opt => {
                    const selected = ctx.csvHasValueCaseInsensitive(
                      ctx.form.specialization,
                      opt.value
                    );
                    return (
                      <div
                        key={opt.value}
                        role="option"
                        aria-selected={selected}
                        className={[
                          'create-user-select-option',
                          'create-user-modality-option',
                          selected ? 'create-user-modality-option--selected-toggle' : '',
                        ]
                          .filter(Boolean)
                          .join(' ')}
                        onMouseDown={e => {
                          e.preventDefault();
                          ctx.setFormField(
                            'specialization',
                            ctx.toggleSpecialtyInCsvCaseInsensitive(
                              ctx.form.specialization,
                              opt.value
                            )
                          );
                          ctx.setSpecializationSearchQuery('');
                        }}
                      >
                        <span
                          className="create-user-modality-option-check"
                          aria-hidden
                        >
                          {selected ? (
                            <Check
                              size={14}
                              strokeWidth={2.5}
                              className="create-user-modality-check"
                            />
                          ) : null}
                        </span>
                        <span className="create-user-modality-option-label">{opt.label}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
          <div className="create-user-dialog-grid">
            <FormField
              label="Registration Number"
              value={ctx.form.registrationNumber || ''}
              onChange={e => ctx.setFormField('registrationNumber', e.target.value)}
              onBlur={ctx.trimFormFieldOnBlur('registrationNumber')}
              placeholder="REG-12345"
            />
            <FormField
              label="Clinic/Hospital Name"
              value={ctx.form.clinicHospitalName || ''}
              onChange={e => ctx.setFormField('clinicHospitalName', e.target.value)}
              onBlur={ctx.trimFormFieldOnBlur('clinicHospitalName')}
              placeholder="Enter clinic or hospital name"
            />
          </div>
          <h3 className="create-user-dialog-section-title create-user-dialog-section-title-with-divider">
            Access Control
          </h3>
          <div className="create-user-dialog-grid">
            <div
              className="create-user-dialog-field create-user-dialog-field-styled-dropdown"
              ref={ctx.reportAccessDropdownRef}
            >
              <RequiredLabel
                label="Report Access"
                required
              />
              <div
                className={`create-user-select-wrap ${ctx.reportAccessDropdownOpen ? 'create-user-select-wrap-open' : ''} ${ctx.touched && ctx.errors.reportAccess ? 'create-user-dialog-input-error' : ''}`}
                onClick={() => ctx.setReportAccessDropdownOpen(!ctx.reportAccessDropdownOpen)}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    ctx.setReportAccessDropdownOpen(!ctx.reportAccessDropdownOpen);
                  }
                  if (e.key === 'Escape') {
                    ctx.setReportAccessDropdownOpen(false);
                  }
                }}
                role="combobox"
                aria-expanded={ctx.reportAccessDropdownOpen}
                aria-haspopup="listbox"
                aria-invalid={!!(ctx.touched && ctx.errors.reportAccess)}
                tabIndex={0}
              >
                <span
                  className={
                    ctx.form.reportAccess
                      ? 'create-user-select-value'
                      : 'create-user-select-placeholder'
                  }
                >
                  {formatReportAccessLabel(ctx.form.reportAccess)}
                </span>
                <ChevronDown
                  className="create-user-select-chevron"
                  size={18}
                  strokeWidth={2}
                  aria-hidden
                />
              </div>
              {ctx.reportAccessDropdownOpen && (
                <div
                  className="create-user-select-dropdown"
                  role="listbox"
                  onMouseDown={e => e.preventDefault()}
                >
                  <div
                    role="option"
                    aria-selected={ctx.form.reportAccess === 'true'}
                    className="create-user-select-option"
                    onMouseDown={e => {
                      e.preventDefault();
                      ctx.setFormField('reportAccess', 'true');
                      ctx.setReportAccessDropdownOpen(false);
                    }}
                  >
                    Yes
                  </div>
                  <div
                    role="option"
                    aria-selected={ctx.form.reportAccess === 'false'}
                    className="create-user-select-option"
                    onMouseDown={e => {
                      e.preventDefault();
                      ctx.setFormField('reportAccess', 'false');
                      ctx.setReportAccessDropdownOpen(false);
                    }}
                  >
                    No
                  </div>
                </div>
              )}
              {ctx.touched && ctx.errors.reportAccess && (
                <span className="create-user-dialog-field-error">{ctx.errors.reportAccess}</span>
              )}
            </div>
          </div>
        </>
      )}
    </>
  );
}

function UserFormSubmitError() {
  const ctx = useUserFormDialogFormCtx();

  return <>{ctx.submitError && <p className="create-user-dialog-error">{ctx.submitError}</p>}</>;
}

const UserFormDialog: React.FC<UserFormDialogProps> = ({
  open,
  mode,
  initialUser,
  facilityId,
  onClose,
  onSuccess,
}) => {
  const [form, setForm] = useState<Record<string, string>>({ ...INITIAL_FORM });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [touched, setTouched] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [modalityDropdownOpen, setModalityDropdownOpen] = useState(false);
  const [roleDropdownOpen, setRoleDropdownOpen] = useState(false);
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const [genderDropdownOpen, setGenderDropdownOpen] = useState(false);
  const [subRoleDropdownOpen, setSubRoleDropdownOpen] = useState(false);
  const [specialtyDropdownOpen, setSpecialtyDropdownOpen] = useState(false);
  /** Filter text for specialty dropdown (separate from CSV so typing does not overwrite chips). */
  const [specialtySearchQuery, setSpecialtySearchQuery] = useState('');
  const [specializationSearchQuery, setSpecializationSearchQuery] = useState('');
  const [reportAccessDropdownOpen, setReportAccessDropdownOpen] = useState(false);
  const [selectedSignatureFileName, setSelectedSignatureFileName] = useState<string>('');
  const signatureFileRef = useRef<File | null>(null);
  const signaturePreviewParentBlobUrlRef = useRef<string | null>(null);
  const [signaturePreviewOpen, setSignaturePreviewOpen] = useState(false);
  const [signaturePreviewSourceUrl, setSignaturePreviewSourceUrl] = useState<string | null>(null);
  const [signaturePreviewOwnedByParent, setSignaturePreviewOwnedByParent] = useState(false);
  const [signaturePreviewFileName, setSignaturePreviewFileName] = useState<string | null>(null);
  const [signaturePreviewMimeHint, setSignaturePreviewMimeHint] = useState<string | null>(null);
  /** Edit/create: user explicitly cleared signature (hide server file + send removal on save). */
  const [signatureCleared, setSignatureCleared] = useState(false);
  const [signatureFileError, setSignatureFileError] = useState<string | null>(null);
  const modalityDropdownRef = useRef<HTMLDivElement>(null);
  const roleDropdownRef = useRef<HTMLDivElement>(null);
  const statusDropdownRef = useRef<HTMLDivElement>(null);
  const genderDropdownRef = useRef<HTMLDivElement>(null);
  const subRoleDropdownRef = useRef<HTMLDivElement>(null);
  const specialtyDropdownRef = useRef<HTMLDivElement>(null);
  const reportAccessDropdownRef = useRef<HTMLDivElement>(null);
  const lastPopulatedKeyRef = useRef<string | null>(null);
  const dobPickerInputRef = useRef<HTMLInputElement>(null);

  const isEdit = mode === 'edit';
  /** Sub-role is required for all Radiologist users */
  const showSubRole = form.role === 'Radiologist';

  const populateFromUser = useCallback((user: User) => {
    const mapped = mapApiResponseToFormValues(user);
    const mobileDigits = (mapped.phoneNo || '').replace(/\D/g, '').slice(-10);
    const dobForm = apiDobToDdMmYyyy(mapped.dateOfBirth || user.date_of_birth);
    setForm({
      role: user.role || '',
      subRole: user.sub_role || mapped.sub_role || '',
      employeeId: mapped.employeeId || '',
      fullName: mapped.fullName || '',
      gender: mapped.gender || '',
      dateOfBirth: dobForm,
      email: mapped.email || '',
      mobileNumber: mobileDigits,
      username: mapped.username || '',
      password: '',
      status: mapped.status === 'active' ? 'Active' : 'Inactive',
      shiftStartAt: (mapped as { shiftStart?: string }).shiftStart || '',
      shiftEndAt: (mapped as { shiftEnd?: string }).shiftEnd || '',
      experience: mapped.experienceYears != null ? String(mapped.experienceYears) : '',
      designation: mapped.designation || '',
      registrationNumber: mapped.registrationNumber || '',
      qualification: mapped.qualification || '',
      specialty: mapped.specialty || '',
      modalityAccess: Array.isArray(mapped.reportingModalityAccess)
        ? mapped.reportingModalityAccess.join(', ')
        : '',
      department: Array.isArray(mapped.department) ? mapped.department.join(', ') : '',
      specialization: mapped.specialization || '',
      clinicHospitalName: mapped.clinicHospitalName || '',
      reportAccess: mapped.reportAccess !== undefined ? String(mapped.reportAccess) : 'true',
      availabilityDays: availabilityDaysDisplayToForm(mapped.availabilityDays || []),
    });
  }, []);

  useEffect(() => {
    if (!open) {
      return;
    } // never reset on close — Dialog may briefly flicker open=false

    // Build a stable key: different per user in edit, or 'add' for create mode
    const currentKey = isEdit && initialUser?.id ? `edit:${initialUser.id}` : 'add';

    // Only populate if this is genuinely a new dialog session for a different target
    if (lastPopulatedKeyRef.current === currentKey) {
      return;
    }
    lastPopulatedKeyRef.current = currentKey;

    setModalityDropdownOpen(false);
    setRoleDropdownOpen(false);
    setStatusDropdownOpen(false);
    setGenderDropdownOpen(false);
    setSubRoleDropdownOpen(false);
    setSpecialtyDropdownOpen(false);
    setSpecialtySearchQuery('');
    setSpecializationSearchQuery('');
    setReportAccessDropdownOpen(false);
    setErrors({});
    setTouched(false);
    setSubmitError(null);
    setSignatureFileError(null);

    if (isEdit && initialUser) {
      populateFromUser(initialUser);
      setSignatureCleared(false);
      setSelectedSignatureFileName(extractFilenameFromPath(initialUser.signature) || '');
      const mapped = mapApiResponseToFormValues(initialUser);
      const rawVal = mapped.reportAccess;
      // Handle string type from mapper, defaulting to true if null/undefined
      setFormField(
        'reportAccess',
        rawVal === 'true' || rawVal == null || rawVal === undefined ? 'true' : 'false'
      );
    } else {
      setForm({ ...INITIAL_FORM });
      setSignatureCleared(false);
      setSelectedSignatureFileName('');
      signatureFileRef.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isEdit, initialUser?.id]); // use initialUser?.id (primitive), NOT the object

  useEffect(() => {
    const anyOpen =
      modalityDropdownOpen ||
      roleDropdownOpen ||
      statusDropdownOpen ||
      genderDropdownOpen ||
      subRoleDropdownOpen ||
      specialtyDropdownOpen ||
      reportAccessDropdownOpen;
    if (!anyOpen) {
      return;
    }
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (modalityDropdownRef.current && !modalityDropdownRef.current.contains(target)) {
        setModalityDropdownOpen(false);
      }
      if (roleDropdownRef.current && !roleDropdownRef.current.contains(target)) {
        setRoleDropdownOpen(false);
      }
      if (statusDropdownRef.current && !statusDropdownRef.current.contains(target)) {
        setStatusDropdownOpen(false);
      }
      if (genderDropdownRef.current && !genderDropdownRef.current.contains(target)) {
        setGenderDropdownOpen(false);
      }
      if (subRoleDropdownRef.current && !subRoleDropdownRef.current.contains(target)) {
        setSubRoleDropdownOpen(false);
      }
      if (specialtyDropdownRef.current && !specialtyDropdownRef.current.contains(target)) {
        setSpecialtyDropdownOpen(false);
      }
      if (reportAccessDropdownRef.current && !reportAccessDropdownRef.current.contains(target)) {
        setReportAccessDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [
    modalityDropdownOpen,
    roleDropdownOpen,
    statusDropdownOpen,
    genderDropdownOpen,
    subRoleDropdownOpen,
    specialtyDropdownOpen,
    reportAccessDropdownOpen,
  ]);

  const filterSpecialtyOptionsByQuery = (searchQuery: string) => {
    const normalizedSearch = searchQuery.trim().toLowerCase();
    if (!normalizedSearch) {
      return SPECIALTY_OPTIONS;
    }
    return SPECIALTY_OPTIONS.filter(
      option =>
        option.label.toLowerCase().includes(normalizedSearch) ||
        option.value.toLowerCase().includes(normalizedSearch)
    );
  };

  const csvHasValueCaseInsensitive = (csv: string, value: string) => {
    const needle = value.trim().toLowerCase();
    if (!needle) {
      return false;
    }
    return parseCsvValues(csv).some(csvToken => csvToken.toLowerCase() === needle);
  };

  const toggleSpecialtyInCsvCaseInsensitive = (csvValue: string, canonicalOptionValue: string) => {
    const current = parseCsvValues(csvValue);
    const needle = canonicalOptionValue.toLowerCase();
    const idx = current.findIndex(entry => entry.toLowerCase() === needle);
    if (idx >= 0) {
      return current.filter((_, i) => i !== idx).join(', ');
    }
    return [...current, canonicalOptionValue].join(', ');
  };

  const shouldShowAddCustomSpecialty = (searchQuery: string, commaSeparatedValues: string) => {
    const trimmedSearchQuery = searchQuery.trim();
    if (!trimmedSearchQuery) {
      return false;
    }
    if (csvHasValueCaseInsensitive(commaSeparatedValues, trimmedSearchQuery)) {
      return false;
    }
    return filterSpecialtyOptionsByQuery(searchQuery).length === 0;
  };

  const appendSpecialtyCustomToCsv = (csvValue: string, customLabelInput: string) => {
    const trimmedCustomLabel = customLabelInput.trim();
    if (!trimmedCustomLabel || csvHasValueCaseInsensitive(csvValue, trimmedCustomLabel)) {
      return csvValue;
    }
    return [...parseCsvValues(csvValue), trimmedCustomLabel].join(', ');
  };

  const specialtyOptionLabelForChip = (storedValueFromCsv: string) =>
    SPECIALTY_OPTIONS.find(
      option => option.value.toLowerCase() === storedValueFromCsv.toLowerCase()
    )?.label ?? storedValueFromCsv;

  const setFormField = (key: string, value: string) => setForm(prev => ({ ...prev, [key]: value }));

  const trimFormFieldOnBlur = useCallback(
    (key: string) => (e: React.FocusEvent<HTMLInputElement>) => {
      const raw = e.target.value;
      const trimmed = raw.trim();
      if (trimmed !== raw) {
        setForm(prev => ({ ...prev, [key]: trimmed }));
      }
    },
    []
  );

  const trimSearchQueryStateOnBlur = useCallback(
    (setter: React.Dispatch<React.SetStateAction<string>>) =>
      (e: React.FocusEvent<HTMLInputElement>) => {
        const raw = e.target.value;
        const t = raw.trim();
        if (t !== raw) {
          setter(t);
        }
      },
    []
  );

  const revokeSignaturePreviewParentBlob = useCallback(() => {
    if (signaturePreviewParentBlobUrlRef.current) {
      URL.revokeObjectURL(signaturePreviewParentBlobUrlRef.current);
      signaturePreviewParentBlobUrlRef.current = null;
    }
  }, []);

  const handleClose = useCallback(() => {
    lastPopulatedKeyRef.current = null; // allow fresh populate on next open
    revokeSignaturePreviewParentBlob();
    setSignaturePreviewOpen(false);
    setSignaturePreviewSourceUrl(null);
    setSignaturePreviewFileName(null);
    setSignaturePreviewMimeHint(null);
    setSignaturePreviewOwnedByParent(false);
    onClose();
  }, [onClose, revokeSignaturePreviewParentBlob]);

  const handleSubmit = useCallback(async () => {
    const f = trimFormStringFields(form, ['password']) as Record<string, string>;
    setForm(f);

    const gate = evaluateUserFormSubmitGate({
      f,
      isEdit,
      initialUserId: initialUser?.id,
      signatureFile: signatureFileRef.current instanceof File ? signatureFileRef.current : null,
      getEditUserValidationErrors,
      getCreateUserValidationErrors,
      isAllowedSignatureUploadFile,
      signatureUploadTypeError: SIGNATURE_UPLOAD_TYPE_ERROR,
    });

    if (!gate.ok) {
      if (gate.kind === 'validation') {
        setErrors(gate.errors);
        setTouched(true);
        setSubmitError(null);
      } else if (gate.kind === 'submitError') {
        setSubmitError(gate.message);
      }
      return;
    }

    setSubmitting(true);
    setSubmitError(null);
    setErrors({});

    const ctx = buildSubmitContext({
      f,
      facilityId: facilityId || undefined,
      initialUser,
      signatureFile: signatureFileRef.current instanceof File ? signatureFileRef.current : null,
      signatureCleared,
      getMobileDigits,
    });

    try {
      const outcome = await runUserFormSubmit({
        isEdit,
        ctx,
        facilityId: facilityId || undefined,
        getDobForPayload,
      });
      if (outcome.abort || !outcome.result) {
        setSubmitError(outcome.error || null);
        return;
      }
      applyUserFormSubmitResult({
        result: outcome.result,
        isEdit,
        onSuccess,
        handleClose,
        clearSignatureFile: () => {
          signatureFileRef.current = null;
        },
        setSubmitError,
      });
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Something went wrong');
    } finally {
      setSubmitting(false);
    }
  }, [form, isEdit, initialUser, facilityId, onSuccess, handleClose, signatureCleared]);

  const handleClearSignature = useCallback(() => {
    revokeSignaturePreviewParentBlob();
    signatureFileRef.current = null;
    setSelectedSignatureFileName('');
    setSignatureFileError(null);
    setSignatureCleared(true);
    setSignaturePreviewOpen(false);
    setSignaturePreviewSourceUrl(null);
    setSignaturePreviewFileName(null);
    setSignaturePreviewMimeHint(null);
    setSignaturePreviewOwnedByParent(false);
    const input = document.getElementById('create-user-signature-file') as HTMLInputElement | null;
    if (input) {
      input.value = '';
    }
  }, [revokeSignaturePreviewParentBlob]);

  const openSignaturePreview = useCallback(() => {
    const file = signatureFileRef.current;
    if (file) {
      revokeSignaturePreviewParentBlob();
      const url = URL.createObjectURL(file);
      signaturePreviewParentBlobUrlRef.current = url;
      setSignaturePreviewSourceUrl(url);
      setSignaturePreviewOwnedByParent(true);
      setSignaturePreviewFileName(file.name);
      setSignaturePreviewMimeHint(file.type || null);
      setSignaturePreviewOpen(true);
      return;
    }
    revokeSignaturePreviewParentBlob();
    if (isEdit && initialUser?.signature && !signatureCleared) {
      const resolved = resolveSignatureAssetUrl(initialUser.signature);
      if (resolved) {
        setSignaturePreviewSourceUrl(resolved);
        setSignaturePreviewOwnedByParent(false);
        setSignaturePreviewFileName(extractFilenameFromPath(initialUser.signature));
        setSignaturePreviewMimeHint(null);
        setSignaturePreviewOpen(true);
      }
    }
  }, [isEdit, initialUser?.signature, signatureCleared, revokeSignaturePreviewParentBlob]);

  const handleSignaturePreviewOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (!nextOpen) {
        revokeSignaturePreviewParentBlob();
        setSignaturePreviewSourceUrl(null);
        setSignaturePreviewFileName(null);
        setSignaturePreviewMimeHint(null);
        setSignaturePreviewOwnedByParent(false);
      }
      setSignaturePreviewOpen(nextOpen);
    },
    [revokeSignaturePreviewParentBlob]
  );

  const canShowClearSignature =
    Boolean(selectedSignatureFileName?.trim()) ||
    Boolean(signatureFileRef.current) ||
    (isEdit && Boolean(initialUser?.signature) && !signatureCleared);

  const canOpenSignaturePreview =
    Boolean(signatureFileRef.current) ||
    Boolean(selectedSignatureFileName?.trim()) ||
    (isEdit && Boolean(initialUser?.signature) && !signatureCleared);

  const disableSubmit =
    submitting ||
    !form.role ||
    (form.role !== 'ReferringPhysician' && !form.employeeId) ||
    !form.fullName ||
    !form.gender ||
    !form.dateOfBirth ||
    !form.email ||
    !form.mobileNumber ||
    !form.username ||
    (isEdit ? false : !form.password) ||
    ((form.role === 'Technician' || form.role === 'Radiologist') &&
      (!form.shiftStartAt?.trim() || !form.shiftEndAt?.trim()));

  const userFormDialogFormCtxValue = {
    open,
    isEdit,
    form,
    setForm,
    setFormField,
    trimFormFieldOnBlur,
    trimSearchQueryStateOnBlur,
    touched,
    errors,
    showSubRole,
    roleDropdownOpen,
    setRoleDropdownOpen,
    roleDropdownRef,
    subRoleDropdownOpen,
    setSubRoleDropdownOpen,
    subRoleDropdownRef,
    genderDropdownOpen,
    setGenderDropdownOpen,
    genderDropdownRef,
    statusDropdownOpen,
    setStatusDropdownOpen,
    statusDropdownRef,
    specialtyDropdownOpen,
    setSpecialtyDropdownOpen,
    specialtyDropdownRef,
    specialtySearchQuery,
    setSpecialtySearchQuery,
    modalityDropdownOpen,
    setModalityDropdownOpen,
    modalityDropdownRef,
    specializationSearchQuery,
    setSpecializationSearchQuery,
    reportAccessDropdownOpen,
    setReportAccessDropdownOpen,
    reportAccessDropdownRef,
    submitting,
    submitError,
    disableSubmit,
    handleSubmit,
    handleClose,
    signatureFileRef,
    selectedSignatureFileName,
    setSelectedSignatureFileName,
    signatureFileError,
    setSignatureFileError,
    signatureCleared,
    setSignatureCleared,
    canShowClearSignature,
    canOpenSignaturePreview,
    openSignaturePreview,
    handleClearSignature,
    dobPickerInputRef,
    shouldShowAddCustomSpecialty,
    appendSpecialtyCustomToCsv,
    specialtyOptionLabelForChip,
    toggleAvailabilityDayValue,
    filterSpecialtyOptionsByQuery,
    csvHasValueCaseInsensitive,
    toggleSpecialtyInCsvCaseInsensitive,
    parseCsvValues,
  };

  const renderDialog = () => (
    <UserFormDialogFormContext.Provider value={userFormDialogFormCtxValue}>
      <>
        <Dialog
          open={open}
          onOpenChange={openState => {
            if (!openState && !submitting) {
              handleClose();
              setTouched(false);
              setErrors({});
            }
          }}
          shouldCloseOnOverlayClick={false}
        >
          <DialogContent
            className={`create-user-dialog-content ${isEdit ? 'edit-user-dialog-content edit-user-dialog-content-full' : ''}`}
          >
            <DialogHeader>
              <DialogTitle className="create-user-dialog-title">
                {isEdit ? 'Edit User' : 'Create New User'}
              </DialogTitle>
              <p className="create-user-dialog-description">
                {isEdit
                  ? 'Update user information and role assignments.'
                  : 'Fill in the details below to create a new user account.'}
              </p>
            </DialogHeader>
            <div className="create-user-dialog-body">
              <UserFormRoleFields />
              <UserFormSubRoleFields />
              <UserFormGeneralDetailsFields />
              <UserFormTechnicianCredentials />
              <UserFormRadiologistCredentials />
              <UserFormReferringPhysicianSection />
              <UserFormSubmitError />
            </div>
            <div className="create-user-dialog-footer">
              <Button
                type="button"
                variant="secondary"
                fullWidth={false}
                className="create-user-dialog-btn create-user-dialog-btn-cancel"
                onClick={handleClose}
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="primary"
                fullWidth={false}
                className="create-user-dialog-btn create-user-dialog-btn-primary"
                onClick={handleSubmit}
                disabled={disableSubmit}
              >
                {submitting
                  ? isEdit
                    ? 'Saving...'
                    : 'Creating...'
                  : isEdit
                    ? 'Save Changes'
                    : 'Create User'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
        <SignaturePreviewDialog
          open={signaturePreviewOpen}
          onOpenChange={handleSignaturePreviewOpenChange}
          sourceUrl={signaturePreviewSourceUrl}
          sourceUrlOwnedByParent={signaturePreviewOwnedByParent}
          fileName={signaturePreviewFileName}
          mimeTypeHint={signaturePreviewMimeHint}
        />
      </>
    </UserFormDialogFormContext.Provider>
  );
  return renderDialog();
};

export default UserFormDialog;
